/* eslint-disable no-shadow */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';
import moment from 'moment';

import { ballTypeIds, coachingTypeIds, officialsTypeIds, utrTypeIds, sportTypeIds, acceptanceBucketTypeSortKey, divisionFormatTypeIds, ANNUAL_POWER_ONLY_LABEL } from '../../../utils/eventConstants';
import {
    eventDivisionIsPlatformPaymentOrFree,
    isPickleBallSportTypeEvent,
    isDinglesDivision,
    isUserEligibleForDivision,
    isDivisionCustomRegistrationDateClosed,
} from '../../../utils/events';
import { getLabelByTypeId } from '../../../utils/types';
import BallType from '../../text/BallType';
import Img from '../../Img';

import styles from './DivisionCardV4.scss';

const noDateAssigned = 'NO DATE ASSIGNED';

// Division card with registration button
function DivisionCardV4(props) {
    const {
        user,
        utrTypeId,
        workflowVersion,
        showDetails,
        clubVerificationFee,
        allowOffPlatformPayments,
        division,
        sportTypeId,
        isUserRegistered,
        showRegisteredText,
        handleRegClick,
        eventIsPublished,
        isRegistrationOpen,
        disableRegisterBtn,
        divisionsLength,
        gridLayout,
        isPtsEvent,
        eventStartUtc,
        showPlayersCount,
        hasDivisionWithSpecificDate,
        onDivisionSelect,
        event,
        timeZone,
        isUserInvited,
        eventSchedule,
        isCurrentMemberAdmin,
        isPreReg,
        doesUserHavePreRegInvite,
        'data-testid': dataTestId,
    } = props;

    const {
        name,
        price,
        priceNonPowered,
        genderId,
        ageRange,
        utrRange,
        annualPoweredOnly,
        ballTypeId,
        eliminationTypeId,
        scoringTypeId,
        coachingTypeId,
        officialsTypeId,
        isWaitlistLocked,
        isTimeBased,
    } = division;

    const [showPoweredLabel, setShowPoweredLabel] = useState(false);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const {
        genderTypes,
        division: {
            tournament: {
                drawEliminationTypes,
                scoringTypes,
                singlePointScoringTypes,
            },
            formatTypes,
        },
    } = coreUI.types.tms;

    const userIsPowered = user?.isPowered;
    const eventDivisionIsPlatformPaymentOrFreeResult = eventDivisionIsPlatformPaymentOrFree(division);
    const showPriceNonPowered = (eventDivisionIsPlatformPaymentOrFreeResult && !allowOffPlatformPayments);
    const isSanctioned = utrTypeId === utrTypeIds.SANCTIONED;
    const isPickleBallEvent = isPickleBallSportTypeEvent(sportTypeId);
    const currentScoringTypes = isPickleBallEvent ? singlePointScoringTypes : scoringTypes;
    const isDivisionDingles = isDinglesDivision(division);
    const isDoubles = division.divisionFormatTypeId !== divisionFormatTypeIds.SINGLES;
    const detail = [];
    const showRegisteredList = division.registeredCount > 0;
    const showWaitlist = !isPtsEvent && division.allowWaitlist && division.waitlistCount > 0;
    const showAlternateList = isPtsEvent && division.alternateCount > 0;
    const showNeedsPartnerList = isDoubles && division.needsPartnerCount > 0;
    const maxCount = isDoubles ? division.maxTeams : division.maxPlayers;
    let registeredCount = division.nonAcceptanceMainCount;
    if (division.isAcceptance) {
        registeredCount = isDoubles ? Math.round(division.registeredCount / 2) : division.registeredCount;
    }
    const isFullDivision = isDoubles && registeredCount !== null ? (maxCount > 0 && registeredCount >= maxCount) : (maxCount > 0 && division.registeredCount >= maxCount);
    const isMainBucketFull = maxCount !== null && registeredCount >= maxCount;

    const isUserEligible = isUserEligibleForDivision(division, user, eventStartUtc, sportTypeId, event);
    const divisionCustomRegistrationIsClosed = isDivisionCustomRegistrationDateClosed(division);
    const divisionCustomRegistrationIsClosedEndDateOnly = isDivisionCustomRegistrationDateClosed(division, true);
    const isCappedZeroDivision = !division.allowWaitlist && maxCount === 0;

    const isRegistrationBtnDisabled = divisionCustomRegistrationIsClosed || ((!eventIsPublished || !isUserEligible || !isRegistrationOpen || disableRegisterBtn || (isFullDivision && !division.allowWaitlist) || divisionCustomRegistrationIsClosed || isCappedZeroDivision) && !(isPreReg && doesUserHavePreRegInvite));

    const isPoweredLabelShown = userIsPowered && isSanctioned && workflowVersion > 1 && clubVerificationFee > 0 && showPriceNonPowered;

    if (eliminationTypeId) {
        detail.push(getLabelByTypeId(drawEliminationTypes, eliminationTypeId));
    }
    if (scoringTypeId) {
        detail.push(getLabelByTypeId(currentScoringTypes, scoringTypeId));
    }
    if (isTimeBased) {
        detail.push('Time Based');
    }
    const additionalDetail = [];
    if (coachingTypeId && coachingTypeId === coachingTypeIds.YES) {
        additionalDetail.push('Coaching');
    }
    if (officialsTypeId && officialsTypeId === officialsTypeIds.YES) {
        additionalDetail.push('Officials');
    }

    const handleDivisionLink = () => {
        onDivisionSelect(division.id);
    };

    const showNeedPartners = showPlayersCount && showNeedsPartnerList;

    const getTextStatus = (isUserInvited) => {
        return isUserInvited ? `Invited` : `Registered`;
    };
    const showDivisionRegDates = division?.registrationEndUtc !== null || division?.registrationStartUtc !== null;
    const now = moment.utc();
    const eventRegStartDate = moment.utc(eventSchedule?.registrationStartUtc);
    const eventRegCloseDate = moment.utc(eventSchedule?.registrationEndUtc);
    const divisionRegStartDate = moment.utc(division?.registrationStartUtc);
    const divisionRegEndDate = moment.utc(division?.registrationEndUtc);
    const showDivisionStartDate = divisionRegStartDate?.isAfter(eventRegStartDate) && now?.isBefore(divisionRegStartDate);
    const showDivisionEndDate = divisionRegEndDate?.isBefore(eventRegCloseDate) && now?.isBefore(divisionRegEndDate);
    let statusLabel = '';
    let dateLabel = null;
    let displayText = <>&nbsp;</>;

    if (showDivisionStartDate) {
        const divRegStartDate = divisionRegStartDate.tz(timeZone?.value).format('MM/DD');
        statusLabel = 'Opens';
        dateLabel = divRegStartDate;
        displayText = <>Opens: {divRegStartDate}</>;
    }
    if (showDivisionEndDate) {
        const divRegEndDate = divisionRegEndDate.tz(timeZone?.value).format('MM/DD');
        statusLabel = 'Early Close';
        dateLabel = divRegEndDate;
        displayText = <>Early Close: {divRegEndDate}</>;
    }

    const hasPreRegInvite = isPreReg && doesUserHavePreRegInvite;
    const showWaitlistOnly = !isCurrentMemberAdmin && user && isUserEligible && (isRegistrationOpen || isPreReg) && (isWaitlistLocked || isMainBucketFull) && (!isRegistrationBtnDisabled || hasPreRegInvite);
    const showClosed = !isCurrentMemberAdmin && user && isUserEligible && (isRegistrationOpen || isPreReg) && divisionCustomRegistrationIsClosedEndDateOnly;

    let divisionRegStateText = <>&nbsp;</>;

    if (showClosed) {
        divisionRegStateText = <span className="text-error">Closed</span>;
    } else if (showWaitlistOnly) {
        divisionRegStateText = <span className="text-warning">Waitlist Only</span>;
    }

    return (
        <>
            <div className={`utr-card ${styles.divisionCardV4} ${styles.mobile}`}>
                <div className={styles.noGutters}>
                    <div className={`${styles.divisionCardHeader}`}>
                        <div className={`${styles.divisionName} text-break`} data-testid={dataTestId && `${dataTestId}.mobile.name`}>
                            {name}
                        </div>
                        <div
                            onMouseEnter={() => setShowPoweredLabel(isPoweredLabelShown)}
                            onMouseLeave={() => setShowPoweredLabel(false)}
                            className={styles.price}
                        >
                            {(isSanctioned && workflowVersion > 1 && clubVerificationFee > 0 && showPriceNonPowered && !isDivisionDingles) ? (
                                <div className={`d-flex flex-col aic`}>
                                    <div
                                        className={styles.priceComparison}
                                    >
                                        <span className={`${(showPoweredLabel || userIsPowered) ? 'line-through' : ''} fw600`}>{priceNonPowered}</span>
                                        <span className={`price-divider`}> / </span>
                                        <span className={`${styles.powerHover} ${styles.poweredTeal} ${!(showPoweredLabel || userIsPowered) ? 'line-through' : ''} fw600`}>
                                            {price}
                                            {<span className={styles.poweredLabelWrapper}>POWER</span>}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <span className="fw600" data-testid={dataTestId && `${dataTestId}.mobile.price`}>{price}</span>
                            )}
                        </div>
                    </div>

                    <div className={`flex-col ais fs12 fw600`} data-testid={dataTestId && `${dataTestId}.draw-details`}>
                        {hasDivisionWithSpecificDate && <div className="d-flex aic">{moment(division.specificDivisionDateUtc).isValid() ? moment.utc(division.specificDivisionDateUtc).tz(timeZone.value).format('MMM D, YYYY') : '-'}</div>}
                        {(!isPickleBallEvent && (ballTypeId !== ballTypeIds.YELLOW)) && <div className={styles.ballType}><BallType ballType={ballTypeId} /></div>}
                        {annualPoweredOnly && <span className="text-bold text-small">{ANNUAL_POWER_ONLY_LABEL}<br /></span>}
                        {showDetails && (
                            <>
                                {detail.length ? detail.join(', ') : ''}
                                {additionalDetail.length ? (<span><br />{additionalDetail.join(', ')}</span>) : ''}
                            </>
                        )}

                    </div>
                    <div className="div mt8 mb12" />
                    <div className={styles.detailsWrapper}>
                        <div className={styles.detailsTable}>
                            <div className={styles.detailsTableItem}>
                                <span className={styles.detailsTableLabel}>
                                    {isPickleBallEvent ? 'UTR-P' : 'UTR'}:&nbsp;
                                </span>
                                {utrRange}
                            </div>
                            <div className={styles.detailsTableItem}>
                                <span className={styles.detailsTableLabel}>Type:&nbsp;</span>
                                {isDoubles ? 'Doubles' : 'Singles'}
                            </div>
                            <div className={styles.detailsTableItem}>
                                <span className={styles.detailsTableLabel}>Gender:&nbsp;</span>
                                {genderId ? getLabelByTypeId(genderTypes, genderId) : '--'}
                            </div>
                            <div className={styles.detailsTableItem}>
                                <span className={styles.detailsTableLabel}>Age:&nbsp;</span>
                                {ageRange}
                            </div>
                        </div>

                        <div className={styles.detailsTable}>
                            {!!maxCount && !division.isAcceptance && (
                                <div className={styles.detailsTableItem}>
                                    <span className={`${styles.detailsTableLabel} ellipsis`}>Max {isDoubles ? 'Teams' : 'Players'}:&nbsp;</span>
                                    {maxCount}
                                </div>
                            )}
                            {showPlayersCount && (
                                <>
                                    {division.isAcceptance ? division.acceptanceBucketTypeIds.map(typeId => {
                                        if (typeId === acceptanceBucketTypeSortKey.MAIN) {
                                            return (
                                                division.mainCount > 0 ? <div className={styles.detailsTableItem}><span className={styles.detailsTableLabel}>Main:&nbsp;</span>{division.mainCount}</div> : null
                                            );
                                        }
                                        if (typeId === acceptanceBucketTypeSortKey.QUALIFYING) {
                                            return (
                                                division.qualifyingCount > 0 ? <div className={styles.detailsTableItem}><span className={styles.detailsTableLabel}>Qualifying:&nbsp;</span>{division.qualifyingCount}</div> : null
                                            );
                                        }
                                        if (typeId === acceptanceBucketTypeSortKey.ALTERNATE) {
                                            return (
                                                division.alternateCount > 0 ? <div className={styles.detailsTableItem}><span className={styles.detailsTableLabel}>Alternate:&nbsp;</span>{division.alternateCount}</div> : null
                                            );
                                        }
                                        return null;
                                    }) : (
                                        <>
                                            {showRegisteredList && registeredCount > 0 && <div className={styles.detailsTableItem}><span className={styles.detailsTableLabel}>Registered:&nbsp;</span> {registeredCount}</div>}
                                            {showWaitlist && <div className={styles.detailsTableItem}><span className={styles.detailsTableLabel}>Waitlist:&nbsp;</span> {division.waitlistCount}</div>}
                                            {showAlternateList && <div className={styles.detailsTableItem}><span className={styles.detailsTableLabel}>Alternate:&nbsp;</span> {division.alternateCount}</div>}
                                        </>
                                    )}
                                </>
                            )}
                            {showNeedPartners && <div className={styles.detailsTableItem}><span className={styles.detailsTableLabel}>Need Partner:&nbsp;</span>{division.needsPartnerCount}</div>}
                        </div>
                    </div>
                    <div className="div mt12 mb12" />

                    {isUserRegistered ? (
                        <div className={styles.registered}>
                            {showRegisteredText ? getTextStatus(isUserInvited) : ''}
                        </div>
                    ) : (
                        <div className={styles.buttonWrapper}>
                            <div className={styles.divisionRegStateText}>
                                {divisionRegStateText}
                            </div>
                            <div className={`d-flex flex-col aic`}>
                                <button
                                    type="button"
                                    className={`btn btn-default btn-sm text-uppercase ${isRegistrationBtnDisabled ? 'disabled' : ''}`}
                                    disabled={isRegistrationBtnDisabled}
                                    onClick={(e) => {
                                        const date = division.specificDivisionDateUtc ? new Date(division.specificDivisionDateUtc).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : noDateAssigned;
                                        handleRegClick(e, division.id, date, divisionsLength);
                                    }}
                                    data-testid={dataTestId && `${dataTestId}.mobile.register-btn`}
                                >
                                    Register
                                </button>
                                {showDivisionRegDates && statusLabel && dateLabel && (
                                    <div className={styles.regDate}>
                                        <span>{statusLabel}:&nbsp;</span>
                                        {dateLabel}
                                    </div>

                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={`utr-card ${styles.divisionCardV4} ${styles.desktop}`} style={{ background: '#fff' }} data-testid={dataTestId ? `${dataTestId}.desktop` : undefined}>
                <div style={{ background: '#fff', width: '100%' }}>
                    <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.map(fr => `minmax(0, ${fr})`).join(' ') }}>
                        <div className="d-flex aic pr8">
                            <div className={styles.noGutters}>
                                <div className={`${styles.divisionName} text-break`} onClick={handleDivisionLink} data-testid={dataTestId && `${dataTestId}.desktop.name`}>
                                    {name}
                                    {/* <a href={`/events/${eventId}?t=2&divisionId=${division.id}`}>{name}</a> */}
                                </div>

                                <div className={``} data-testid={dataTestId && `${dataTestId}.desktop.draw-details`}>
                                    {(!isPickleBallEvent && (ballTypeId !== ballTypeIds.YELLOW)) && <div className={styles.ballType}><BallType ballType={ballTypeId} /></div>}
                                    {annualPoweredOnly && <span className="text-bold text-small">{ANNUAL_POWER_ONLY_LABEL}<br /></span>}
                                    {showDetails && (
                                        <>
                                            {detail.length ? detail.join(', ') : ''}
                                            {additionalDetail.length ? (<span><br />{additionalDetail.join(', ')}</span>) : ''}
                                        </>
                                    )}
                                </div>

                                <div className={styles.regCount}>
                                    {!!maxCount && !division.isAcceptance && <div className="pr16">{isDoubles ? `Max teams: ${maxCount}` : `Max players: ${maxCount}`}</div>}
                                    {
                                        showPlayersCount && (
                                            <>
                                                {division.isAcceptance ? division.acceptanceBucketTypeIds.map(typeId => {
                                                    if (typeId === acceptanceBucketTypeSortKey.MAIN) {
                                                        return (
                                                            division.mainCount > 0 ? <div className="pr16">Main: {division.mainCount}</div> : null
                                                        );
                                                    }
                                                    if (typeId === acceptanceBucketTypeSortKey.QUALIFYING) {
                                                        return (
                                                            division.qualifyingCount > 0 ? <div className="pr16">Qualifying: {division.qualifyingCount}</div> : null
                                                        );
                                                    }
                                                    if (typeId === acceptanceBucketTypeSortKey.ALTERNATE) {
                                                        return (
                                                            division.alternateCount > 0 ? <div className="pr16">Alternate: {division.alternateCount}</div> : null
                                                        );
                                                    }
                                                    return null;
                                                }) : (
                                                    <>
                                                        {showRegisteredList && registeredCount > 0 && <div className="pr16">Registered: {registeredCount}</div>}
                                                        {showWaitlist && <div className="pr16">Waitlist: {division.waitlistCount}</div>}
                                                        {showAlternateList && <div className="pr16">Alternate: {division.alternateCount}</div>}
                                                    </>
                                                )}
                                            </>
                                        )
                                    }
                                    {showNeedPartners && <div className="pr16">Need partner: {division.needsPartnerCount}</div>}
                                </div>
                                {isCurrentMemberAdmin && isWaitlistLocked && (
                                    <div className="fs14 mt8 text-danger d-flex aic gap-8">
                                        <Img src="/img/v4/lock-red.svg" /> Waitlist Frozen
                                    </div>
                                )}
                            </div>
                        </div>

                        {hasDivisionWithSpecificDate && <div className="d-flex aic pr8">{moment(division.specificDivisionDateUtc).isValid() ? moment.utc(division.specificDivisionDateUtc).tz(timeZone.value).format('MMM D, YYYY') : '-'}</div>}
                        <div className="d-flex aic pr8">{formatTypes.find(type => type.id === parseInt(division.divisionFormatTypeId))?.label}</div>
                        <div className="d-flex aic pl4" data-testid={dataTestId && `${dataTestId}.desktop.utr-range`}>{utrRange}</div>
                        <div className="d-flex aic pl4" data-testid={dataTestId && `${dataTestId}.desktop.age-range`}>{ageRange}</div>
                        <div className="d-flex aic pr8" data-testid={dataTestId && `${dataTestId}.desktop.gender`}>{genderId ? getLabelByTypeId(genderTypes, genderId) : '--'}</div>
                        <div
                            onMouseEnter={() => setShowPoweredLabel(isPoweredLabelShown)}
                            onMouseLeave={() => setShowPoweredLabel(false)}
                            className="d-flex aic pr8"
                        >
                            {(isSanctioned && workflowVersion > 1 && clubVerificationFee > 0 && showPriceNonPowered && !isDivisionDingles) ? (
                                <div className={`d-flex flex-col aic`}>
                                    <div
                                        className={styles.priceComparison}
                                    >
                                        <span className={`${(showPoweredLabel || userIsPowered) ? 'line-through' : ''}`}>{priceNonPowered}</span>
                                        <span className={`price-divider`}> / </span>
                                        <span className={`${styles.powerHover} ${styles.poweredTeal} ${!(showPoweredLabel || userIsPowered) ? 'line-through' : ''}`}>
                                            {price}
                                            {<span className={styles.poweredLabelWrapper}>POWER</span>}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <span className={``} data-testid={dataTestId && `${dataTestId}.desktop.price`}>{price}</span>
                            )}
                        </div>

                        {isUserRegistered ? (
                            <div className="d-flex aic jcc">
                                <div className={styles.registered}>
                                    {showRegisteredText ? getTextStatus(isUserInvited) : ''}
                                </div>
                            </div>
                        ) : (
                            <div className={`d-flex flex-column aic jce' : 'aic jce'}`}>
                                <div className={`flex-1 mb8 fs12 fw500`}>
                                    {divisionRegStateText}
                                </div>
                                <div className="flex-1">
                                    <button
                                        type="button"
                                        className={`btn btn-default btn-sm text-uppercase ${isRegistrationBtnDisabled ? 'disabled' : ''}`}
                                        disabled={isRegistrationBtnDisabled}
                                        onClick={(e) => {
                                            const date = division.specificDivisionDateUtc ? new Date(division.specificDivisionDateUtc).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : noDateAssigned;
                                            handleRegClick(e, division.id, date, divisionsLength);
                                        }}
                                        data-testid={dataTestId && `${dataTestId}.desktop.register-btn`}
                                    >
                                        Register
                                    </button>
                                </div>
                                <div className="flex-1 mt8 fs12 text-bold no-wrap">
                                    {showDivisionRegDates ? displayText : <>&nbsp;</>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

DivisionCardV4.propTypes = {
    user: PropTypes.objectOf(any).isRequired,
    utrTypeId: PropTypes.number,
    workflowVersion: PropTypes.number,
    showDetails: PropTypes.bool,
    clubVerificationFee: PropTypes.number,
    allowOffPlatformPayments: PropTypes.bool,
    division: PropTypes.objectOf(any).isRequired,
    sportTypeId: PropTypes.number,
    isUserRegistered: PropTypes.bool,
    showRegisteredText: PropTypes.bool,
    handleRegClick: PropTypes.func,
    eventIsPublished: PropTypes.bool,
    disableRegisterBtn: PropTypes.bool,
    divisionsLength: PropTypes.number,
    gridLayout: PropTypes.arrayOf(any),
    isPtsEvent: PropTypes.bool,
    isRegistrationOpen: PropTypes.bool,
    eventStartUtc: PropTypes.string,
    showPlayersCount: PropTypes.bool,
    hasDivisionWithSpecificDate: PropTypes.bool,
    onDivisionSelect: PropTypes.func,
    event: PropTypes.objectOf(any).isRequired,
    timeZone: PropTypes.objectOf(any).isRequired,
    isUserInvited: PropTypes.bool,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    isCurrentMemberAdmin: PropTypes.bool,
};

DivisionCardV4.defaultProps = {
    utrTypeId: utrTypeIds.MYUTR,
    workflowVersion: 1,
    showDetails: false,
    clubVerificationFee: 0,
    allowOffPlatformPayments: false,
    sportTypeId: sportTypeIds.TENNIS,
    isUserRegistered: false,
    showRegisteredText: false,
    handleRegClick: () => { },
    eventIsPublished: false,
    disableRegisterBtn: false,
    divisionsLength: 0,
    gridLayout: [],
    isPtsEvent: false,
    isRegistrationOpen: false,
    eventStartUtc: '',
    showPlayersCount: true,
    hasDivisionWithSpecificDate: false,
    onDivisionSelect: () => { },
    isUserInvited: false,
    isCurrentMemberAdmin: false,
};

export default DivisionCardV4;
